import React, { useCallback, useMemo, useState } from 'react';
import loadable from '@loadable/component';
import { useHandleProgressChanges } from './use-handle-progress-changes';
import { useStoredVideoPosition } from './use-stored-video-position';
import { ImagePlaceholder, Placeholder, PreloaderPlayButton, Shadow } from './VideoPlayer.styles';
import { VideoPlayerLayout } from './VideoPlayerLayout';
import SvgVideo from '../../../../play-core/icons/Video';
import { Colors } from '../../../../play-core/styles/Colors';
import { idToUrn } from '../../../../play-core/utils/urn-helper';
import { ErrorBoundary } from '../../../src/pages/Error/ErrorBoundary';
import { LbwError } from '../../../src/pages/Error/LbwError';
import { usePrivacyState } from '../../../src/privacy/PrivacyProvider/PrivacyContext';
import { useVideoConfig } from '../../config/VideoAppContext';
import { useDelay } from '../../hooks/use-delay';
import { defaultSegmentHeight } from '../Letterbox/Letterbox.constants';
import { YouthProtectionOverlay, youthProtectionOverlaySlideInDuration } from '../YouthProtection/YouthProtectionOverlay';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Letterbox = loadable(() => import('../Letterbox/Letterbox').then(mod => mod.Letterbox), {
  ssr: false
});
export const VideoPlayer = ({
  id,
  urn,
  escMediaComposition,
  placeholderImage,
  hasSegments = false,
  size,
  trackProgress = true,
  seekPosition,
  autoPlay = false,
  isEmbedded = false,
  hideShareOptions = false,
  hideSegments,
  onSegmentVisibilityChanged,
  onProgressChanged,
  youthProtectionColor,
  onDataPartLoaded,
  onLoaded,
  onPlay,
  onPause,
  ...rest
}) => {
  const [youthProtectionOverlay, setYouthProtectionOverlay] = useState('slide-out');
  const {
    start: startYouthProtectionOverlayDelay,
    cancel: cancelYouthProtectionOverlayDelay
  } = useDelay();
  const [isPlayerLoaded, setPlayerLoaded] = useState(false);
  const {
    ilHost,
    businessUnit,
    language,
    supportStandalonePlayer
  } = useVideoConfig();
  const [segmentsVisible, setSegmentsVisible] = useState(hasSegments);
  const [segmentsHeight, setSegmentsHeight] = useState(hasSegments ? defaultSegmentHeight : 0);
  const {
    isRequired,
    consent,
    isConsentGiven
  } = usePrivacyState();
  const isPrivacySatisfied = !isRequired || isConsentGiven;
  const hideYouthProtectionOverlay = useCallback(() => {
    setYouthProtectionOverlay('slide-out');
    cancelYouthProtectionOverlayDelay();
  }, [cancelYouthProtectionOverlayDelay]);
  const playbackUrn = useMemo(() => {
    if (urn) {
      hideYouthProtectionOverlay();
      return urn;
    }
    if (id) {
      hideYouthProtectionOverlay();
      return idToUrn(id, businessUnit, 'video');
    }
    if (escMediaComposition) {
      return escMediaComposition.chapterUrn;
    }
  }, [hideYouthProtectionOverlay, urn, escMediaComposition, id, businessUnit]);
  const position = useStoredVideoPosition(playbackUrn, seekPosition, trackProgress);
  const handleProgressChanged = useHandleProgressChanges(trackProgress, playbackUrn, onProgressChanged);
  const handleSegmentVisibilityChanged = useCallback((isVisible, height) => {
    if (isVisible !== undefined && height !== undefined) {
      setSegmentsVisible(isVisible);
      setSegmentsHeight(height);
      onSegmentVisibilityChanged && onSegmentVisibilityChanged(isVisible, height);
    }
  }, [onSegmentVisibilityChanged]);
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(VideoPlayerLayout, {
      isLoading: !playbackUrn,
      size: size,
      segmentsVisible: isEmbedded ? false : segmentsVisible,
      segmentViewHeight: segmentsHeight,
      isEmbedded: isEmbedded,
      children: /*#__PURE__*/_jsxs(ErrorBoundary, {
        errorComponent: /*#__PURE__*/_jsx(LbwError, {}),
        children: [playbackUrn && isPrivacySatisfied && /*#__PURE__*/_jsx(Letterbox, {
          urn: playbackUrn,
          escMediaComposition: escMediaComposition,
          ilHost: ilHost,
          seekPosition: position,
          language: language,
          autoPlay: autoPlay,
          onProgressChanged: handleProgressChanged,
          onSegmentVisibilityChanged: handleSegmentVisibilityChanged,
          standalone: isEmbedded && supportStandalonePlayer,
          hideSegments: hideSegments,
          hideShareOptions: hideShareOptions,
          showPlayIcon: isEmbedded,
          onDataPartLoaded: (...arg) => {
            onDataPartLoaded && onDataPartLoaded(...arg);
            if (!isPlayerLoaded) {
              setPlayerLoaded(true);
            }
          },
          onError: (...arg) => {
            onLoaded && onLoaded(...arg);
            hideYouthProtectionOverlay();
            setPlayerLoaded(true);
          },
          suppressTracking: isRequired && consent?.['TagCommander Reporting'] !== true,
          onPlay: (...e) => {
            onPlay && onPlay(...e);
            if (youthProtectionColor) {
              setYouthProtectionOverlay('slide-in');
              startYouthProtectionOverlayDelay(() => setYouthProtectionOverlay('slide-out'), youthProtectionOverlaySlideInDuration);
            }
          },
          onPause: (...e) => {
            onPause && onPause(...e);
            hideYouthProtectionOverlay();
          },
          ...rest,
          children: youthProtectionColor && /*#__PURE__*/_jsx(YouthProtectionOverlay, {
            youthProtectionColor: youthProtectionColor,
            state: youthProtectionOverlay
          })
        }), placeholderImage && /*#__PURE__*/_jsxs(Placeholder, {
          hasSegments: hasSegments,
          isLoaded: isPlayerLoaded,
          children: [/*#__PURE__*/_jsx(PreloaderPlayButton, {
            icon: /*#__PURE__*/_jsx(SvgVideo, {
              orientation: "right",
              color: Colors.GREY_d2d2d2
            })
          }), /*#__PURE__*/_jsx(ImagePlaceholder, {
            src: placeholderImage,
            width: 960,
            mediumImageWidth: 320,
            xLargeImageWidth: 960,
            alt: placeholderImage,
            hasBorderRadius: false,
            priorize: true
          }), /*#__PURE__*/_jsx(Shadow, {})]
        })]
      })
    })
  });
};